/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, NavLink, useLocation } from 'react-router';

import { Button } from '@mui/material';

import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { useConfirmationPageStyles } from './ConfirmationPage.styles';

type PageData = {
  titleText: string | React.ReactNode;
  descriptionText?: string | React.ReactNode;
  buttonText: string | React.ReactNode;
  buttonLink: string;
};

export function ConfirmationPage() {
  const classes = useConfirmationPageStyles();
  const { t } = useTranslation([Namespaces.ConfirmationPage]);
  const location = useLocation();

  const pageData = useMemo<PageData | undefined>(() => {
    switch (location.pathname) {
      case BasicRoutes.CONFIRM_PASSWORD_RESET:
        return {
          titleText: t('titles.confirm_password_reset'),
          descriptionText: t('descriptions.confirm_password_reset'),
          buttonText: t('buttons.confirm_password_reset'),
          buttonLink: BasicRoutes.LOGIN,
        };
      default:
        return undefined;
    }
  }, [location.pathname, t]);

  if (!pageData) {
    return <Navigate to={BasicRoutes.HOME} />;
  }

  const { buttonText, buttonLink, descriptionText, titleText } = pageData;

  return (
    <div className={classes.root}>
      <h3 className={classes.title}>{titleText}</h3>
      {descriptionText && <h5 className={classes.subTitle}>{descriptionText}</h5>}
      <div className={classes.linkContainer}>
        <NavLink to={buttonLink} className={classes.link}>
          <Button type="submit" variant="contained" size="large">
            {buttonText}
          </Button>
        </NavLink>
      </div>
    </div>
  );
}
